import { useCallback, useEffect, useState } from 'react';
import { useAction } from '@/lib/api-hooks';
import toast from 'react-hot-toast';
import { RefreshCw, Upload } from 'lucide-react';
import { backendApi } from '@/lib/backend';
import { useModuleAccess } from '@/lib/useModuleAccess';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Loader } from '@/components/ui/Loader';
import { ProvisioningTable } from './ProvisioningTable';

type Phone = {
  id: number;
  mac: string;
  model?: string;
  template_name?: string;
  assigned_ext_num?: string;
};

interface PhonesTabProps {
  customerId: string;
}

export function PhonesTab({ customerId }: PhonesTabProps) {
  const { canManage } = useModuleAccess('phones');

  const phonesList = useAction(backendApi.provisioning.phonesList);
  const phonesProvision = useAction(backendApi.provisioning.phonesProvision);
  const phonesReprovisionAll = useAction(backendApi.provisioning.phonesReprovisionAll);

  const [phones, setPhones] = useState<Phone[]>([]);
  const [loading, setLoading] = useState(true);
  const [showBulk, setShowBulk] = useState(false);
  const [bulkCsv, setBulkCsv] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const rows = await phonesList({ customerId, source: 'ui' });
      setPhones(rows);
    } catch (error) {
      setPhones([]);
      toast.error(error instanceof Error ? error.message : 'Failed to load phones');
    } finally {
      setLoading(false);
    }
  }, [customerId, phonesList]);

  useEffect(() => {
    void load();
  }, [load]);

  function parseCsv() {
    return bulkCsv
      .split('\n')
      .map((l) => l.trim())
      .filter(Boolean)
      .map((line) => {
        const [mac, model, extension] = line.split(',').map((s) => s.trim());
        return { mac, model, extension };
      })
      .filter((i) => i.mac && i.model && i.extension);
  }

  async function handleProvision() {
    const items = parseCsv();
    if (items.length === 0) {
      toast.error('Enter at least one valid mac,model,extension row');
      return;
    }
    try {
      const data = await phonesProvision({ customerId, source: 'ui', items });
      const parts = [
        `Created: ${data.created?.length ?? 0}`,
        `Updated: ${data.updated?.length ?? 0}`,
        `Skipped: ${data.skipped?.length ?? 0}`,
      ];
      if (data.manualSteps?.length) parts.push(`Manual: ${data.manualSteps.length} DECT device(s)`);
      toast.success(`Provisioning complete — ${parts.join(', ')}`);
      if (data.manualSteps?.length) {
        alert(data.manualSteps.map((s: { instructions: string }) => s.instructions).join('\n\n'));
      }
      setShowBulk(false);
      setBulkCsv('');
      void load();
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Provision failed');
    }
  }

  async function reprovisionAll() {
    if (!confirm('Reprovision all phones?')) return;
    try {
      const data = await phonesReprovisionAll({ customerId, source: 'ui' });
      toast.success(`Reprovision queued for ${data.count} phone(s)`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'Reprovision failed');
    }
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Phones</h3>
          <p className="text-sm text-gray-500 dark:text-gray-400">{phones.length} auto-provisioned phones</p>
        </div>
        <div className="flex gap-2">
          <Button variant="ghost" size="sm" onClick={() => void load()}>
            <RefreshCw className="h-4 w-4" />
          </Button>
          {canManage && (
            <>
              <Button variant="secondary" size="sm" onClick={() => void reprovisionAll()}>
                Reprovision all
              </Button>
              <Button size="sm" onClick={() => setShowBulk(!showBulk)}>
                <Upload className="h-4 w-4 mr-1" />
                Provision batch
              </Button>
            </>
          )}
        </div>
      </div>

      {canManage && showBulk && (
        <Card animate={false}>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            CSV: mac,model,extension
          </label>
          <textarea
            value={bulkCsv}
            onChange={(e) => setBulkCsv(e.target.value)}
            rows={6}
            placeholder="C4:FC:22:44:83:45,SIP-T77U,100"
            className="w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white font-mono text-sm p-3 mb-3"
          />
          <div className="flex gap-2">
            <Button onClick={() => void handleProvision()}>Execute</Button>
            <Button variant="secondary" onClick={() => setShowBulk(false)}>
              Cancel
            </Button>
          </div>
        </Card>
      )}

      <Card animate={false} className="p-0 overflow-hidden">
        {loading ? (
          <Loader text="Loading phones..." />
        ) : (
          <ProvisioningTable
            columns={[
              { key: 'mac', label: 'MAC' },
              { key: 'model', label: 'Model' },
              { key: 'template', label: 'Template' },
              { key: 'ext', label: 'Extension' },
            ]}
            rows={phones.map((p) => ({
              mac: <span className="font-mono text-xs">{p.mac}</span>,
              model: p.model ?? '—',
              template: p.template_name ?? '—',
              ext: p.assigned_ext_num ?? '—',
            }))}
            emptyMessage="No phones provisioned."
          />
        )}
      </Card>
    </div>
  );
}